import React, { useState, useEffect, useMemo } from 'react';
import { isValidNhsNumber } from '../../utils/nhsValidation';
import {
  Button,
  TextField,
  Box,
  Typography,
  Select,
  MenuItem,
} from '@mui/material';
import { UseFormRegister, FieldErrors, Control, Controller } from 'react-hook-form';
import { Patient } from '../../services/patientService';
import patientFieldConfigService, { PatientFieldConfig } from '../../services/patientFieldConfigService';
import { PatientType } from '../../services/patientTypeService';
import { useTenant } from '../../context/TenantContext';
import { ClinicDialog } from './ClinicDialog';
import { CLINIC_DIALOG_SX, POPUP_COLORS } from '../../constants/popupStyles';
import { DynamicField, groupConfigsIntoTabs } from './dynamicFields';

const textFieldInputProps = { style: CLINIC_DIALOG_SX.textField as React.CSSProperties };

interface PatientFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  editingPatient?: Patient | null;
  register: UseFormRegister<any>;
  errors: FieldErrors;
  /** react-hook-form formState.submitCount — used to jump to the errored tab on failed save. */
  submitCount?: number;
  control: Control<any>;
  defaultCountryCode?: string;
  clinicState?: string | null;
  fieldErrors?: { id_number?: string };
  onClearFieldError?: (field: keyof { id_number?: string }) => void;
  dynamicFieldConfigs?: PatientFieldConfig[];
  watchStatus?: string;
  patientTypeId?: number;
  patientTypes?: PatientType[];
  onPatientTypeChange?: (typeId: number | undefined) => void;
}

type StaticTab = 'overview';
type Tab = StaticTab | string;

const STATIC_TAB_LABELS: Record<string, StaticTab> = {
  'Overview': 'overview',
};

export const PatientFormDialog: React.FC<PatientFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  editingPatient,
  register,
  errors,
  submitCount = 0,
  control,
  defaultCountryCode = '+44',
  clinicState,
  fieldErrors = {},
  onClearFieldError,
  dynamicFieldConfigs = [],
  watchStatus,
  patientTypeId,
  patientTypes = [],
  onPatientTypeChange,
}) => {
  const { currentTenant } = useTenant();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [dynamicTabs, setDynamicTabs] = useState<{ name: string; order: number; configs: PatientFieldConfig[] }[]>([]);
  const [staticExtensions, setStaticExtensions] = useState<Map<StaticTab, PatientFieldConfig[]>>(new Map());
  const editConfigsRef = React.useRef<PatientFieldConfig[]>([]);

  // Fetch field configs filtered by patient type when provided
  useEffect(() => {
    if (!currentTenant) return;
    patientFieldConfigService.getAll(currentTenant.id, patientTypeId).then(configs => {
      const editConfigs = configs.filter(c => c.display_in_edit && c.edit_tab_name);
      editConfigsRef.current = editConfigs;
      const grouped = groupConfigsIntoTabs(editConfigs, STATIC_TAB_LABELS);
      setStaticExtensions(grouped.staticExtensions as Map<StaticTab, PatientFieldConfig[]>);
      setDynamicTabs(grouped.dynamicTabs);
    }).catch(() => {});
  }, [currentTenant, patientTypeId]);

  useEffect(() => {
    if (open) setActiveTab('overview');
  }, [open]);

  // Map each dynamic field key -> the tab it lives on (static-extension fields stay on overview).
  const fieldKeyToTab = useMemo(() => {
    const m: Record<string, string> = {};
    dynamicTabs.forEach(t => t.configs.forEach(c => { m[c.key] = t.name; }));
    staticExtensions.forEach((cfgs, staticKey) => cfgs.forEach(c => { m[c.key] = staticKey; }));
    return m;
  }, [dynamicTabs, staticExtensions]);

  // On each failed save (submitCount increments), if the current tab has no error but another
  // tab does, jump to the first errored tab so the hidden validation error becomes visible.
  // Keyed off submitCount because react-hook-form's `errors` object reference can be stable.
  useEffect(() => {
    if (!submitCount) return;
    const tabs = new Set<string>();
    Object.keys(errors || {}).forEach((key) => {
      if (key === 'additional_fields') {
        const afErrors = (errors.additional_fields as Record<string, unknown>) || {};
        Object.keys(afErrors).forEach((afKey) => tabs.add(fieldKeyToTab[afKey] || 'overview'));
      } else {
        tabs.add('overview');
      }
    });
    if (tabs.size === 0 || tabs.has(activeTab)) return;
    const target = tabs.has('overview')
      ? 'overview'
      : dynamicTabs.map(t => t.name).find(n => tabs.has(n)) || Array.from(tabs)[0];
    if (target) setActiveTab(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]);

  const selectSx = (hasValue: boolean) => ({
    ...CLINIC_DIALOG_SX.select,
    color: hasValue ? CLINIC_DIALOG_SX.fieldLabel.color : '#999',
  });

  const tabStyle = (tab: string) => ({
    px: 2,
    py: 1,
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: activeTab === tab ? 600 : 400,
    color: activeTab === tab ? POPUP_COLORS.titleText : '#888',
    borderBottom: activeTab === tab ? `2px solid ${POPUP_COLORS.titleText}` : '2px solid transparent',
    background: 'none',
    border: 'none',
    borderBottomStyle: 'solid',
    borderBottomWidth: '2px',
    borderBottomColor: activeTab === tab ? POPUP_COLORS.titleText : 'transparent',
    transition: 'all 0.15s',
    marginRight: '16px',
    paddingLeft: 0,
    paddingRight: 0,
  } as React.CSSProperties);

  const actions = (
    <>
      <Button type="submit" variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
        {editingPatient ? 'Update' : 'Add'}
      </Button>
      <Button onClick={onClose} sx={CLINIC_DIALOG_SX.cancelButton}>
        Cancel
      </Button>
    </>
  );

  return (
    <ClinicDialog
      open={open}
      onClose={onClose}
      title={editingPatient ? 'Edit Patient' : 'Add New Patient'}
      onSubmit={onSubmit}
      actions={actions}
    >
      {/* Tabs */}
      <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(54,106,117,0.15)', mb: 2, gap: 0 }}>
        <button type="button" onClick={() => setActiveTab('overview')} style={tabStyle('overview')}>
          Overview
        </button>
        {dynamicTabs.map((dt) => (
          <button key={dt.name} type="button" onClick={() => setActiveTab(dt.name)} style={tabStyle(dt.name)}>
            {dt.name}
          </button>
        ))}
      </Box>

      <Box sx={{ display: 'grid' }}>

        {/* ── Overview Tab ── */}
        <Box sx={{ gridArea: '1/1', display: 'flex', flexDirection: 'column', gap: 2, visibility: activeTab === 'overview' ? 'visible' : 'hidden' }}>
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>First Name</Typography>
              <TextField
                {...register('first_name', { required: 'First name is required' })}
                fullWidth size="small"
                error={!!errors.first_name}
                helperText={errors.first_name?.message as string}
                InputProps={textFieldInputProps}
              />
            </Box>

            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Last Name</Typography>
              <TextField
                {...register('last_name', { required: 'Last name is required' })}
                fullWidth size="small"
                error={!!errors.last_name}
                helperText={errors.last_name?.message as string}
                InputProps={textFieldInputProps}
              />
            </Box>

            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Email</Typography>
              <TextField
                {...register('email', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                fullWidth size="small" type="email"
                error={!!errors.email}
                helperText={errors.email?.message as string}
                InputProps={textFieldInputProps}
              />
            </Box>

            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Phone</Typography>
              <TextField
                {...register('phone', {
                  validate: (value) => {
                    if (!value) return true;
                    const digits = value.replace(/\D/g, '');
                    if (digits.length < 7) return 'Phone number is too short';
                    if (digits.length > 15) return 'Phone number is too long';
                    if (!/^[+0-9][\d\s\-().]+$/.test(value.trim())) return 'Invalid phone number';
                    return true;
                  },
                })}
                fullWidth size="small"
                placeholder={`e.g. ${defaultCountryCode}7700900000`}
                error={!!errors.phone}
                helperText={errors.phone?.message as string}
                InputProps={textFieldInputProps}
              />
            </Box>

            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Date of Birth</Typography>
              <TextField
                {...register('date_of_birth', { required: 'Date of birth is required' })}
                fullWidth size="small" type="date"
                error={!!errors.date_of_birth}
                helperText={errors.date_of_birth?.message as string}
                InputProps={textFieldInputProps}
              />
            </Box>

            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Start Medication</Typography>
              <TextField
                {...register('start_medication')}
                fullWidth size="small" type="date"
                error={!!errors.start_medication}
                helperText={errors.start_medication?.message as string}
                InputProps={textFieldInputProps}
              />
            </Box>

            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                {clinicState === 'IL' ? 'ID Number' : 'NHS Number'}
              </Typography>
              <TextField
                {...register('id_number', {
                  validate: (v) => {
                    if (!v) return true;
                    if (clinicState === 'IL' && !/^\d{9}$/.test(v))
                      return 'Israeli ID must be exactly 9 digits';
                    if (clinicState === 'UK' && !isValidNhsNumber(v))
                      return 'Invalid NHS number — please check the number';
                    return true;
                  },
                  onChange: () => {
                    if (fieldErrors.id_number && onClearFieldError) onClearFieldError('id_number');
                  },
                })}
                fullWidth size="small"
                placeholder={clinicState === 'IL' ? '9-digit ID number' : clinicState === 'UK' ? '10-digit NHS number' : undefined}
                error={!!errors.id_number || !!fieldErrors.id_number}
                helperText={(errors.id_number?.message as string) || fieldErrors.id_number}
                InputProps={textFieldInputProps}
              />
            </Box>

            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={{ ...CLINIC_DIALOG_SX.fieldLabel, minWidth: '140px' }}>
                Gender
              </Typography>
              <Controller
                name="gender"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select {...field} fullWidth size="small" displayEmpty sx={selectSx(!!field.value)}>
                    <MenuItem value="" disabled>Select gender</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                    <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
                  </Select>
                )}
              />
            </Box>
            {patientTypes.length > 0 && onPatientTypeChange && (
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={{ ...CLINIC_DIALOG_SX.fieldLabel, minWidth: '140px' }}>
                  Patient Type
                </Typography>
                <Select
                  value={patientTypeId ?? ''}
                  onChange={(e) => onPatientTypeChange(e.target.value ? Number(e.target.value) : undefined)}
                  fullWidth
                  size="small"
                  displayEmpty
                  sx={selectSx(patientTypeId !== undefined && patientTypeId !== null)}
                >
                  <MenuItem value="" disabled>Select patient type</MenuItem>
                  {patientTypes.map((pt) => (
                    <MenuItem key={pt.id} value={pt.id}>{pt.name}</MenuItem>
                  ))}
                </Select>
              </Box>
            )}
            {(staticExtensions.get('overview') ?? []).map((config) => {
              if (config.display_condition?.field === 'status' && watchStatus) {
                if (!config.display_condition.values.includes(watchStatus)) return null;
              }
              return (
                <DynamicField key={`${config.key}-${editingPatient?.id ?? 'new'}`} config={config} register={register} control={control} defaultValue={editingPatient?.additional_fields?.[config.key]} errors={errors} />
              );
            })}
        </Box>

        {/* ── Dynamic Tabs — keyed on patient id so Controllers remount with fresh defaults on patient change ── */}
        {dynamicTabs.map((dt) => (
          <Box key={`${dt.name}-${editingPatient?.id ?? 'new'}`} sx={{ gridArea: '1/1', display: 'flex', flexDirection: 'column', gap: 2, visibility: activeTab === dt.name ? 'visible' : 'hidden' }}>
            {dt.configs.map((config) => {
              if (config.display_condition?.field === 'status' && watchStatus) {
                if (!config.display_condition.values.includes(watchStatus)) return null;
              }
              return (
                <DynamicField
                  key={config.key}
                  config={config}
                  register={register}
                  control={control}
                  defaultValue={editingPatient?.additional_fields?.[config.key]}
                  errors={errors}
                />
              );
            })}
          </Box>
        ))}

      </Box>
    </ClinicDialog>
  );
};
